import Check from '../Check';
import Report from '../Report';
import { MessageType } from '../Message';
import { Result } from '../Result';
import { groupBy, flatMap } from 'lodash';
import type { IFSM } from '../../../stores/model/metamodel/fsm';
import type { IMethod } from '../../../stores/model/metamodel/method';

/**
 * Check for non-determinism of an FSM
 */
class FSMNonDeterminismCheck extends Check<IFSM> {
  static description = 'Check FSM non-determinism';

  check(): Report {
    this.report = new Report();

    const fsm = this.element;

    for (const state of fsm.states) {
      // .slice() is important because Array.is(ObservableArray) yields false
      // and flatMap will not flatten it. This is a limitation of MobX <= 4, but
      // we cannot upgrade without dropping support for IE11.
      const outgoingTransitionMethods = flatMap(state.outgoingTransitions, t => t.methods.slice());
      // `TransitionMethod.method` is a maybe-null `modelReference` that reads
      // back as the loose late-ref type; in a consistent FSM it always resolves
      // to a concrete method, so we read it as `IMethod` the same way the
      // metamodel's own `Transition.metaMethods` getter does.
      const groupedByMethod = groupBy(outgoingTransitionMethods, tm => (tm.method as IMethod).id);
      for (const group of Object.values(groupedByMethod)) {
        const method = group[0].method as IMethod;
        if (group.length > 1) {
          this.report.addMessage(
            MessageType.WARNING,
            `Non-determinism found in ${fsm.displayString} at state ${state.name} for method ${
              method.name
            }: ${group.map(tm => tm.transition.displayString).join(', ')}.`,
            this,
            this.element
          );
        }
      }
    }

    if (this.report.result === Result.NO_RESULT) {
      this.report.addMessage(
        MessageType.INFORMATION,
        `No non-determinism found.`,
        this,
        this.element
      );
    }

    return this.report;
  }
}

export default FSMNonDeterminismCheck;
